import { Column } from 'components/DataGrid';
import type { GridRow, GridSummaryRow, RowOrGroup } from '../../types';
import { eachMonthOfInterval, format, formatISO, startOfMonth } from 'date-fns';
import { byKind, type FieldBranch } from '../columns/createPlanColumn';
import { View } from './View';
import { Edit } from './Edit';
import { Box } from '@ff-it/ui';
import { fmt } from 'utilities';
import { expenseForeground, incomeForeground, monthColumn } from '../columns/style.css';
import type { Interval } from 'core/types';

type MonthDelivery = GridRow['delivery'][string];

function formatDelivery(d: MonthDelivery | undefined): string {
  if (!d) return '';
  const base = `${d.quantity ?? ''}/${d.total ?? ''}`;
  return d.alt_prices ? `${base} (${d.alt_quantity ?? ''}/${d.alt_total ?? ''})` : base;
}

/** One month's editable branch. All month columns share `field: 'delivery'`, so
 * a copied month's payload is compatible with every month cell — copy/paste
 * across columns lands the value at the *target* column's month. copy/paste key
 * by `monthString` because the field is the whole `delivery` dict, not the month. */
function deliveryBranch(monthString: string): FieldBranch<GridRow> {
  return {
    field: 'delivery',
    view: (props) => <View {...props} month={monthString} />,
    edit: (props) => <Edit {...props} month={monthString} />,
    displayCellContent: true,
    clipboard: {
      scalar: (row) => formatDelivery(row.delivery[monthString]),
      copy: (row) => row.delivery[monthString] ?? null,
      paste: (payload, { row }) => {
        if (payload.field !== 'delivery') return null;
        const next = { ...row.delivery };
        if (payload.value == null) {
          delete next[monthString];
        } else {
          next[monthString] = payload.value as MonthDelivery;
        }
        return { id: row.id, delivery: next };
      },
    },
  };
}

export function createMonthColums(interval: Interval): Column<RowOrGroup, GridSummaryRow>[] {
  return eachMonthOfInterval(interval).map((date) => {
    const month = startOfMonth(date);

    const monthString = formatISO(month, { representation: 'date' });
    const key = `month_${monthString}`;

    const branch = deliveryBranch(monthString);

    return byKind({
      key,
      name: format(month, 'MMM. yyyy'),
      className: monthColumn,
      width: 130,
      branches: {
        media: branch,
        service: branch,
        smart: {
          field: 'delivery',
          view: (props) => <View {...props} month={monthString} />,
        },
      },
      renderSummary: ({ row }) => {
        const monthSum = row.summary.months[monthString];
        if (monthSum) {
          return (
            <>
              <Box display="flex" alignItems="center" className={incomeForeground} data-testid="income">
                {fmt(monthSum.income)}
              </Box>
              <Box display="flex" alignItems="center" className={expenseForeground} data-testid="expense">
                {fmt(monthSum.expense)}
              </Box>
            </>
          );
        }
        return null;
      },
    });
  });
}
